import { Box, Image, Shimmer, Typography } from "@jewlr/storybook/core"
import { HiddenInput } from "@jewlr/storybook/form"
import PropTypes from "prop-types"
import React, { useEffect } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import withProductAddons from "areas/product/hoc/with-product-addons"
import {
  toggleAddonChoice,
  updateChoiceDirectly,
} from "areas/product/store/actions"
import { to_currency } from "helpers/number"

const AddonLabel = styled.label`
  align-items: center;
  border: 1px solid ${(props) => props.theme.colors.grey[200]};
  cursor: pointer;
  display: flex;
  padding-left: 10px;
  &:focus-within {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 0;
  }
`

const StyledCheck = styled(Box)`
  height: 18px;
  margin-right: 6px;
  position: relative;
  width: 18px;
  &:before {
    border: solid ${(props) => props.theme.colors.white};
    border-width: 0 2px 2px 0;
    content: "";
    display: ${(props) => (props.checked ? "block" : "none")};
    height: 9px;
    left: 6px;
    position: absolute;
    top: 3px;
    transform: rotate(45deg);
    width: 5px;
    z-index: 2;
  }
  &:after {
    background: ${(props) =>
      props.checked ? props.theme.colors.black : props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.black};
    border-radius: 50%;
    content: "";
    height: 17px;
    position: absolute;
    width: 17px;
    z-index: 1;
  }
`

const AddonContainer = styled(Box).attrs((props) => ({
  bg: "white",
  borderTop: `1px solid ${props.theme.colors.grey[10]}`,
  pt: 2,
  ...props,
}))``

const InternalProductAddons = ({
  addons,
  adminMode,
  header,
  lid,
  line_item,
  prefStore,
  selectedAddons,
  toggleAddon,
  updateChoice,
  wizardLoading,
}) => {
  const hideAddons =
    !addons?.length || wizardLoading || header.params_olid_present || adminMode

  useEffect(() => {
    if (
      !hideAddons &&
      lid &&
      Object.keys(line_item?.product_addons || {}).length > 0
    ) {
      updateChoice({
        disabled: true, // To skip processing addon choice in other sagas
        pref: "addon",
        value: Object.keys(line_item.product_addons),
      })
    }
  }, [hideAddons, lid, line_item])

  if (hideAddons) {
    return null
  }
  return (
    <AddonContainer aria-labelledby="product-addons-heading" role="group">
      <Typography
        as="h2"
        fontSize="18px"
        fontWeight="600"
        id="product-addons-heading"
        mb={2}
      >
        Add-ons
      </Typography>
      {addons.map((addon) => {
        const selected = selectedAddons.includes(addon.set_key)

        return (
          <Box key={`addon-${addon.id}`} mb={2}>
            {!prefStore || (lid && !line_item) ? (
              <Shimmer height="122px" width="100%" />
            ) : (
              <AddonLabel
                aria-label={addon.display_name}
                htmlFor={`product-addon-${addon.set_key}`}
              >
                <HiddenInput
                  aria-checked={selected}
                  checked={selected}
                  data-style-code={addon.style_code}
                  id={`product-addon-${addon.set_key}`}
                  onChange={() => toggleAddon(addon.set_key)}
                  type="checkbox"
                />
                <StyledCheck aria-hidden checked={selected} />
                <Image
                  alt={addon.display_name}
                  height="120"
                  ml="10px"
                  mr="12px"
                  width="120"
                  {...(addon.custom_image
                    ? {
                        src: addon.custom_image,
                      }
                    : {
                        fallback: addon.images[0],
                        srcSet: addon.images[1],
                      })}
                />
                <Box fontSize="14px" lineHeight="14px">
                  <Typography
                    as="h3"
                    fontSize="14px"
                    fontWeight="600"
                    mb="12px"
                  >
                    {addon.display_name}
                  </Typography>
                  <Typography fontWeight="500">
                    {to_currency(addon.surcharge)}
                  </Typography>
                </Box>
              </AddonLabel>
            )}
          </Box>
        )
      })}
    </AddonContainer>
  )
}

InternalProductAddons.propTypes = {
  addons: PropTypes.array,
  adminMode: PropTypes.bool,
  header: PropTypes.object,
  lid: PropTypes.string,
  line_item: PropTypes.object,
  prefStore: PropTypes.bool,
  selectedAddons: PropTypes.array,
  toggleAddon: PropTypes.func,
  updateChoice: PropTypes.func,
  wizardLoading: PropTypes.string,
}

const mapStateToProps = (state) => {
  const lid = state.wizardUI.lid
  let line_item = null

  if (lid) {
    line_item = (state.cart.line_items || []).find((li) => li.id === lid)
  }

  const prefStore = state.wizardUI.prefStore
  const wizardLoading = state.wizard.loading

  return {
    adminMode: state.wizard.adminMode,
    header: state.wizard.header,
    lid,
    line_item,
    prefStore,
    wizardLoading,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    toggleAddon: (setKey) => dispatch(toggleAddonChoice(setKey)),
    updateChoice: (data) => dispatch(updateChoiceDirectly("addon", data)),
  }
}

const ProductAddons = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalProductAddons)

export default withProductAddons(ProductAddons)
